import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Plus, Edit2, Trash2, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { groupActionItems } from '../sortUtils';

const STATUS = [
  { key: 'open', label: 'Open', color: '#3B82F6' },
  { key: 'in_progress', label: 'In Progress', color: '#FFB74D' },
  { key: 'done', label: 'Done', color: '#43A047' },
  { key: 'cancelled', label: 'Cancelled', color: '#8E85A0' },
];

const empty = () => ({ title: '', description: '', assigned_to_member_id: '', meeting_id: '', due_date: '', status: 'open' });

export default function ActionItemsTab({ api, isAdmin, members, onChange }) {
  const [list, setList] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = filter ? `?status=${filter}` : '';
      const [a, m] = await Promise.all([api.get(`/board/action-items${q}`), api.get('/board/meetings')]);
      setList(a.data); setMeetings(m.data);
    } catch (e) { /* noop */ }
  }, [api, filter]);
  useEffect(() => { load(); }, [load]);

  const memberName = (id) => members.find(m => m.id === id)?.name || '—';
  const meetingTitle = (id) => meetings.find(m => m.id === id)?.title || '';

  const openCreate = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty(), ...a }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/board/action-items/${editing.id}`, form);
      else await api.post('/board/action-items', form);
      toast.success('Saved'); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const del = async (a) => {
    if (!window.confirm('Delete this item?')) return;
    try { await api.delete(`/board/action-items/${a.id}`); toast.success('Deleted'); load(); onChange?.(); } catch (e) { toast.error('Failed'); }
  };
  const quickStatus = async (a, status) => {
    try { await api.put(`/board/action-items/${a.id}`, { status }); toast.success(status === 'done' ? 'Marked done' : 'Updated'); load(); onChange?.(); } catch (e) { toast.error('Failed'); }
  };

  const today = new Date().toISOString().slice(0, 10);

  const renderItem = (a) => {
    const s = STATUS.find(x => x.key === a.status) || STATUS[0];
    const overdue = a.due_date && a.due_date < today && a.status !== 'done' && a.status !== 'cancelled';
    return (
      <Card key={a.id} className={`bg-white ${overdue ? 'border-[#D9534F]' : 'border-[#DED8EB]'}`} data-testid={`action-${a.id}`}>
        <CardContent className="p-3 flex items-start gap-3">
          <input type="checkbox" checked={a.status === 'done'} onChange={() => quickStatus(a, a.status === 'done' ? 'open' : 'done')}
            className="mt-1 h-4 w-4 accent-[#6B3FA0]" data-testid={`action-check-${a.id}`} />
          <div className="flex-1 min-w-0">
            <p className={`font-medium ${a.status === 'done' ? 'line-through text-[#8E85A0]' : 'text-[#2D1B4E]'}`}>{a.title}</p>
            {a.description && <p className="text-xs text-[#5A4D6E] mt-0.5">{a.description}</p>}
            <div className="flex flex-wrap items-center gap-x-3 mt-1 text-[10px] text-[#8E85A0]">
              {a.assigned_to_member_id && <span>👤 {memberName(a.assigned_to_member_id)}</span>}
              {a.due_date && <span className={overdue ? 'text-[#D9534F] font-bold' : ''}>📅 {a.due_date}{overdue ? ' · OVERDUE' : ''}</span>}
              {a.meeting_id && <span>📋 {meetingTitle(a.meeting_id)}</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge style={{ background: `${s.color}22`, color: s.color, borderColor: s.color }}>{s.label}</Badge>
            {isAdmin && (
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(a)} className="h-6 w-6"><Edit2 className="h-3 w-3" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(a)} className="h-6 w-6 text-[#D9534F]"><Trash2 className="h-3 w-3" /></Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const buckets = React.useMemo(() => groupActionItems(list), [list]);
  const sections = [
    { key: 'overdue', label: 'Overdue', color: '#D9534F', icon: AlertTriangle, items: buckets.overdue },
    { key: 'due_soon', label: 'Due This Week', color: '#FFB74D', icon: Clock, items: buckets.due_soon },
    { key: 'open', label: 'Open', color: '#3B82F6', icon: CheckCircle, items: buckets.open },
    { key: 'done', label: 'Completed', color: '#43A047', icon: CheckCircle, items: buckets.done },
  ];

  return (
    <div className="space-y-4" data-testid="actions-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-[#2D1B4E]">Action Items</h2>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="border border-[#DED8EB] rounded px-2 py-1.5 text-xs bg-white" data-testid="action-status-filter">
            <option value="">All</option>
            {STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {isAdmin && <Button onClick={openCreate} className="bg-[#6B3FA0]" data-testid="add-action-btn"><Plus className="h-4 w-4 mr-1" /> Add Item</Button>}
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-center py-12 text-[#8E85A0] text-sm">No action items.</p>
      ) : (
        <div className="space-y-5">
          {sections.map(section => {
            if (section.items.length === 0) return null;
            const Icon = section.icon;
            return (
              <div key={section.key} className="space-y-2" data-testid={`action-section-${section.key}`}>
                <h3 className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: section.color }}>
                  <Icon className="h-3.5 w-3.5" /> {section.label}
                  <Badge style={{ background: `${section.color}22`, color: section.color, borderColor: section.color }} className="text-[10px]">
                    {section.items.length}
                  </Badge>
                </h3>
                {section.items.map(renderItem)}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Action Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="a-title" /></div>
            <div><Label className="text-xs">Description</Label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full border border-[#DED8EB] rounded px-2 py-1 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Assignee</Label>
                <select value={form.assigned_to_member_id} onChange={e => setForm(f => ({ ...f, assigned_to_member_id: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white" data-testid="a-assignee">
                  <option value="">—</option>{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
              <div className="col-span-2"><Label className="text-xs">Linked Meeting</Label>
                <select value={form.meeting_id} onChange={e => setForm(f => ({ ...f, meeting_id: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white">
                  <option value="">—</option>{meetings.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                </select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Status</Label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white">
                  {STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#6B3FA0]" data-testid="save-action-btn">{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
